import {
  isColorRangeFormatting,
  isFormattingFilter,
  isSystemFilter,
  type ColorRangeFormatting,
  type FormattingFilter,
} from '@ardoq/api-types';
import type {
  EnhancedScopeData,
  FormattingAndLabelRules,
} from '@ardoq/data-model';
import {
  createColorRangeColorResolver,
  getColorRangeFieldValue,
} from '@ardoq/filter-helpers';
import type { FilterColorData } from '@ardoq/scope-data';
import type { I18nLocale } from '@ardoq/i18n';
import {
  getActiveConditionalFormattingForLegendPure,
  type ActiveConditionalFormattingForLegend,
} from '@ardoq/view-legend';
import type {
  NumericRange,
  SurfaceColoring,
  XyzDataPoint,
} from '@ardoq/xyz-charts';

/** Samples of a gradient rule's scale handed to the chart, enough that no step shows. */
const GRADIENT_SAMPLES = 128;

type GradientRule = FormattingFilter & { value: ColorRangeFormatting };

const isGradientRule = (rule: FormattingFilter): rule is GradientRule =>
  isColorRangeFormatting(rule.value);

/**
 * The rules a user set that color components, which are the only ones a surface of components can
 * show. Not the system rule that grays whatever they leave uncolored.
 */
export const toComponentColorRules = (
  rules: FormattingAndLabelRules['rules']
): FormattingFilter[] =>
  rules.filter(
    (rule): rule is FormattingFilter =>
      isFormattingFilter(rule) &&
      Boolean(rule.affectComponent) &&
      !isSystemFilter(rule)
  );

/** The color the system rule gives components no other rule colors, when there is one. */
export const toUnformattedColor = (
  rules: FormattingAndLabelRules['rules']
): string | null =>
  rules.find(
    (rule): rule is FormattingFilter =>
      isFormattingFilter(rule) && isSystemFilter(rule)
  )?.color ?? null;

const toRange = (values: number[]): NumericRange => [
  Math.min(...values),
  Math.max(...values),
];

const isSameColor = (a: string, b: string | undefined): boolean =>
  a.toLowerCase() === b?.toLowerCase();

const toGradientColoring = (
  rule: GradientRule,
  colorsById: Map<string, string>,
  enhancedScopeData: EnhancedScopeData
): SurfaceColoring | null => {
  const colorOf = createColorRangeColorResolver(
    rule,
    rule.value,
    enhancedScopeData,
    true
  );
  /* Only the components this rule colored: the color a component took is the rule's for its
     value, rather than the system gray for one the rule leaves out. */
  const valuesById = new Map(
    [...colorsById].flatMap(([componentId, color]) => {
      const component = enhancedScopeData.componentsById[componentId];
      const value = component
        ? getColorRangeFieldValue(rule.name, component, enhancedScopeData)
        : undefined;
      return value !== undefined && isSameColor(color, colorOf(value))
        ? [[componentId, value] as const]
        : [];
    })
  );
  if (valuesById.size === 0) return null;

  const domain = toRange([...valuesById.values()]);
  const [low, high] = domain;
  const colors = Array.from(
    { length: GRADIENT_SAMPLES },
    (_, index) =>
      colorOf(low + ((high - low) * index) / (GRADIENT_SAMPLES - 1)) || null
  );

  return { kind: 'gradient', valuesById, domain, colors };
};

/**
 * What colors the surface, from the conditional formatting that colors its components.
 *
 * - No plotted component colored by a rule: the height ramp, as before.
 * - One component rule, and it is a gradient: the rule's field interpolated across the plane and
 *   colored by its scale, so the surface reads the same as the components do elsewhere.
 * - Anything else - list values, single colors, several rules: each component's resolved color
 *   over the part of the plane nearest to it, since those colors name categories.
 */
export const toSurfaceColoring = ({
  points,
  rules,
  filterColorData,
  enhancedScopeData,
}: {
  points: XyzDataPoint[];
  rules: FormattingAndLabelRules['rules'];
  filterColorData: FilterColorData;
  enhancedScopeData: EnhancedScopeData;
}): SurfaceColoring => {
  const colorsById = new Map(
    points.flatMap(({ componentId }) => {
      const color = filterColorData.entities[componentId]?.color;
      return color ? [[componentId, color] as const] : [];
    })
  );
  const unformattedColor = toUnformattedColor(rules);
  const isColoredByARule = [...colorsById.values()].some(
    color => !unformattedColor || !isSameColor(color, unformattedColor)
  );
  if (!isColoredByARule) return { kind: 'height' };

  const componentRules = toComponentColorRules(rules);
  const [onlyRule] = componentRules;
  const gradient =
    componentRules.length === 1 && isGradientRule(onlyRule)
      ? toGradientColoring(onlyRule, colorsById, enhancedScopeData)
      : null;

  return gradient ?? { kind: 'regions', colorsById };
};

/** What a field chart's legend lists when conditional formatting colors it. */
export type FieldColoringLegendData = {
  activeConditionalFormatting: ActiveConditionalFormattingForLegend;
  /** What Ardoq paints components no rule colors, when it paints them at all. */
  unformattedColor: string | null;
};

export const toFieldColoringLegendData = (
  enhancedScopeData: EnhancedScopeData,
  rules: FormattingAndLabelRules['rules'],
  i18nLocale: I18nLocale
): FieldColoringLegendData => ({
  activeConditionalFormatting: getActiveConditionalFormattingForLegendPure(
    enhancedScopeData,
    toComponentColorRules(rules),
    i18nLocale,
    true
  ),
  unformattedColor: toUnformattedColor(rules),
});
